import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The rest of Grade 3.
 *
 * `arithmetic.ts`, `fractions.ts` and `geometry.ts` teach the Grade 3 spine
 * shared with the grades above. These lessons are the year's own work: turning
 * multiplication facts into strategy, meeting a fraction as a *number*, reading
 * a clock and a graph, and naming a shape by what is true of it.
 */
export const GRADE_THREE_LESSONS: Lesson[] = [
  /* ------------------------------------- multiplication and division ----- */
  lesson({
    id: 'multiplication-properties-01',
    unitId: 'unit-multiplication-strategies',
    conceptId: 'multiplication-properties',
    title: 'Properties of Multiplication',
    summary: 'Order does not matter, grouping does not matter — and a fact you do not know can be built from two you do.',
    objectives: [
      'Use the commutative property to halve the facts you must learn',
      'Regroup three factors to make a calculation easier',
      'Break a factor into tens and ones and multiply each piece',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Three facts about multiplication that make it easier',
        blocks: [
          text('Multiplication has three habits that never let you down. They are not extra things to memorise — they are permissions. Each one lets you replace a calculation you cannot do with one you can.'),
          text('That is what a property *is* in mathematics: a promise that a certain rearrangement is safe.'),
        ],
      },
      intuition: {
        title: 'Why order cannot matter',
        blocks: [
          text('Draw 4 rows of 7 dots. Now turn the page a quarter turn. You are looking at 7 rows of 4 dots — and you have not added or removed a single dot.'),
          math('4 \\times 7 = 7 \\times 4'),
          insight('This halves the multiplication table. If you know 7 × 4, you already know 4 × 7; you have never had to learn it separately.'),
          text('Grouping behaves the same way. With three factors you may multiply any two of them first, because the array simply becomes a box of dots and it does not care which way you slice it.'),
          math('(2 \\times 3) \\times 5 = 2 \\times (3 \\times 5)'),
          text('The third property is the useful one. A number can be split, and each piece multiplied separately:'),
          math('7 \\times 13 = 7 \\times 10 + 7 \\times 3'),
          warning('Both pieces get multiplied. Writing 7 × 10 + 3 is the single most common error here, and it is off by 18.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'array', rows: 4, columns: 7, label: '4 × 7' }, 'Turn it a quarter turn and it is 7 × 4. Same dots.'),
          figure({ kind: 'area-model', rows: 7, columns: 13, shadedRows: 7, shadedColumns: 10 }, 'The shaded part is 7 × 10; the strip beside it is 7 × 3.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 7 × 13 without a written method.'),
          steps([
            { label: 'Split the number you find hard.', latex: '7 \\times (10 + 3)' },
            { label: 'Multiply the first piece.', latex: '7 \\times 10 = 70' },
            { label: 'Multiply the second piece.', latex: '7 \\times 3 = 21', note: 'This is the step people forget.' },
            { label: 'Add the two products.', latex: '70 + 21 = 91' },
          ]),
          tip('Split the number into pieces you already know. 13 into 10 and 3 works because 7 × 10 is instant and 7 × 3 is a fact you have. Splitting into 6 and 7 would be legal and useless.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiplication-properties', 4)] },
      independent: { title: 'On your own', blocks: [practice('multiplication-properties', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multiplication-properties', 3, 'Choose the split that makes the arithmetic easiest.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Order never changes a product: a × b = b × a.',
            'You may group three factors either way.',
            'A factor can be split, and every piece must be multiplied.',
            'Split into pieces whose facts you already know.',
          ]),
          insight('The splitting property is not a Grade 3 trick you will grow out of. It is the same rule that expands (x + 2)(x + 3) in Algebra I, met early and in friendly numbers.'),
        ],
      },
    },
  }),

  lesson({
    id: 'multiply-by-tens-01',
    unitId: 'unit-multiplication-strategies',
    conceptId: 'multiply-by-tens',
    title: 'Multiplying by Tens',
    summary: 'Multiplying by 30 is multiplying by 3 and then by 10. The digits stay put; the places shift.',
    objectives: [
      'Multiply a single digit by a multiple of ten or a hundred',
      'Multiply two multiples of ten',
      'Explain why the zeros appear rather than counting them by habit',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The easiest hard multiplication',
        blocks: [
          text('4 × 30 looks like a bigger job than 4 × 3. It is not. It is 4 × 3 with one extra step, and the extra step is the one you already learned in place value.'),
        ],
      },
      intuition: {
        title: 'Where the zero comes from',
        blocks: [
          text('30 is 3 tens. So 4 × 30 is 4 lots of 3 tens, which is 12 tens. And 12 tens is 120.'),
          math('4 \\times 30 = 4 \\times 3 \\times 10 = 12 \\times 10 = 120'),
          insight('The zero is not "added on". It appears because the answer is counted in *tens* rather than in ones, and place value writes that with a zero in the ones column.'),
          text('Two multiples of ten multiply the same way, only now there are two tens to put back — and ten tens is a hundred.'),
          math('20 \\times 40 = 2 \\times 4 \\times 10 \\times 10 = 8 \\times 100 = 800'),
          warning('Counting zeros as a ritual works until it does not. 50 × 40 has two zeros in the question and three in the answer, because 5 × 4 = 20 brings a zero of its own.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'place-value', digits: '120', places: ['Hundreds', 'Tens', 'Ones'] }, '12 tens, rewritten as 1 hundred and 2 tens.'),
          table(['Calculation', 'Front digits', 'Tens to restore', 'Answer'], [
            ['4 × 30', '4 × 3 = 12', '× 10', '120'],
            ['4 × 300', '4 × 3 = 12', '× 100', '1,200'],
            ['20 × 40', '2 × 4 = 8', '× 100', '800'],
            ['50 × 40', '5 × 4 = 20', '× 100', '2,000'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 6 × 700.'),
          steps([
            { label: 'Multiply the leading digits.', latex: '6 \\times 7 = 42' },
            { label: 'Name what you set aside.', latex: '700 = 7 \\times 100' },
            { label: 'Restore it.', latex: '42 \\times 100 = 4200' },
          ]),
          tip('Check the size before you write it down. 6 × 700 must be a bit under 6 × 1000, so an answer in the thousands is right and an answer of 420 is not.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiply-by-tens', 4)] },
      independent: { title: 'On your own', blocks: [practice('multiply-by-tens', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multiply-by-tens', 3, 'Work backwards to the missing factor.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Multiply the front digits, then restore the tens.',
            'Ten times ten is a hundred — two zeros, not one.',
            'Estimate the size of the answer as a check.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'fact-families-01',
    unitId: 'unit-multiplication-strategies',
    conceptId: 'fact-families',
    title: 'Fact Families',
    summary: 'Multiplication and division are one fact read in two directions — which is how a missing number gets found.',
    objectives: [
      'Write the four facts in a family from three numbers',
      'Find a missing factor by dividing',
      'Recognise when a missing number needs division rather than subtraction',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Three numbers, four facts',
        blocks: [
          text('Take 6, 7 and 42. Those three numbers hold four true statements, and every one of them says the same thing.'),
          math('6 \\times 7 = 42 \\quad 7 \\times 6 = 42 \\quad 42 \\div 6 = 7 \\quad 42 \\div 7 = 6'),
          text('That set is called a fact family. Knowing one member means knowing all four.'),
        ],
      },
      intuition: {
        title: 'Division is a question about multiplication',
        blocks: [
          text('"What is 42 ÷ 6?" is a slow way of asking "6 times what makes 42?". They are the same question, and the second one is usually the easier to answer.'),
          insight('This is why division has no facts of its own to learn. Every division fact is a multiplication fact you already know, asked backwards.'),
          text('It also settles what to do with a box in the middle of a calculation. When the numbers are being multiplied, the way back is to divide.'),
          math('8 \\times \\square = 56 \\quad \\longrightarrow \\quad \\square = 56 \\div 8 = 7'),
          warning('Subtracting is the classic wrong move here. 56 − 8 = 48, and 8 × 48 is nowhere near 56. Ask what *undoes* the operation in front of you.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'array', rows: 6, columns: 7, label: '6 × 7 = 42' }, 'Read along the rows or down the columns: the same 42 dots.'),
          table(['If you know', 'You also know'], [
            ['6 × 7 = 42', '7 × 6 = 42'],
            ['6 × 7 = 42', '42 ÷ 6 = 7'],
            ['6 × 7 = 42', '42 ÷ 7 = 6'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the missing number: 9 × □ = 72.'),
          steps([
            { label: 'Rewrite it as the division it really is.', latex: '\\square = 72 \\div 9' },
            { label: 'Ask the multiplication question.', latex: '9 \\times ? = 72' },
            { label: 'Recall the fact.', latex: '9 \\times 8 = 72' },
            { label: 'Check by putting it back.', latex: '9 \\times 8 = 72 \\;\\checkmark' },
          ]),
          tip('Always put your answer back into the original. It costs three seconds and catches every arithmetic slip you are likely to make.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fact-families', 4)] },
      independent: { title: 'On your own', blocks: [practice('fact-families', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('fact-families', 3, 'Now the missing number is the divisor.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Three numbers make four facts.',
            'Division asks a multiplication question backwards.',
            'A missing factor is found by dividing, never by subtracting.',
            'Check by substituting your answer back in.',
          ]),
          insight('The box in "9 × □ = 72" becomes an x in Grade 5. Nothing about the reasoning changes — only the notation.'),
        ],
      },
    },
  }),

  /* ------------------------------------------ patterns and problems ------ */
  lesson({
    id: 'number-patterns-01',
    unitId: 'unit-problem-solving',
    conceptId: 'number-patterns',
    title: 'Patterns in Numbers',
    summary: 'A sequence has a rule, and a rule predicts a number nobody has written down yet.',
    objectives: [
      'Find the rule of a sequence and continue it',
      'Test a rule against every term, not just the first two',
      'Explain why a product is odd or even without multiplying',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Guessing, but not really',
        blocks: [
          text('What comes after 4, 8, 12, 16? Almost everyone says 20 immediately. It is worth slowing down and asking *how* you knew, because the answer is the whole skill.'),
          text('You found a rule — "add 4" — checked it against what was there, and then used it to reach past the end of the list.'),
        ],
      },
      intuition: {
        title: 'A rule must fit every step',
        blocks: [
          text('The dangerous part is stopping after one check. Look at 2, 4, 8. "Add 2" fits the first step and fails the second; "double" fits both. One matching step is a coincidence.'),
          insight('Test your rule on every pair of neighbours you can see. A rule that survives all of them is worth predicting with.'),
          text('Patterns also hide inside the multiplication table. Every multiple of 4 is even — and so is every multiple of any even number, because you are counting in groups that can each be split in two.'),
          math('\\text{even} \\times \\text{anything} = \\text{even}'),
          text('A product is odd only when *both* factors are odd. One even factor is enough to make the whole thing even.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'number-line', from: 0, to: 20, step: 4, marks: [{ at: 4, label: '4' }, { at: 8, label: '8' }, { at: 12, label: '12' }, { at: 16, label: '16' }] }, 'Equal jumps: the rule is the size of the jump.'),
          figure({ kind: 'array', rows: 2, columns: 7, label: '2 × 7 = 14' }, 'Two equal rows, so the total splits in half exactly. That is what "even" means.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Continue the pattern: 7, 13, 19, 25, □'),
          steps([
            { label: 'Find the first gap.', latex: '13 - 7 = 6' },
            { label: 'Check the next.', latex: '19 - 13 = 6' },
            { label: 'And the last.', latex: '25 - 19 = 6', note: 'Three checks, not one.' },
            { label: 'Apply the rule once more.', latex: '25 + 6 = 31' },
          ]),
          tip('Write the gaps underneath the sequence. If they are all the same you have an adding rule; if they grow, look for a multiplying one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('number-patterns', 4)] },
      independent: { title: 'On your own', blocks: [practice('number-patterns', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('number-patterns', 3, 'Some of these double rather than add.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Find the rule, then check it against every step.',
            'Equal gaps mean adding; growing gaps mean multiplying.',
            'One even factor makes the product even.',
            'A product is odd only when both factors are odd.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'two-step-problems-01',
    unitId: 'unit-problem-solving',
    conceptId: 'two-step-problems',
    title: 'Two-Step Word Problems',
    summary: 'Some questions cannot be answered until a different question has been answered first.',
    objectives: [
      'Identify the hidden first question in a word problem',
      'Carry out two operations in the right order',
      'Check that the answer given is the one the question asked for',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The number that is not in the question',
        blocks: [
          text('"Maya buys 4 boxes of 6 pencils. She already had 5 pencils. How many now?" There is no single sum here that gives the answer, because the number you need — how many pencils were in the boxes — has not been written down.'),
          text('Two-step problems all have that shape. You must make a number before you can use it.'),
        ],
      },
      intuition: {
        title: 'Find the middle number, then finish',
        blocks: [
          text('Read the question and ask: what is the very last thing that happens? For Maya, the last thing is combining the new pencils with the old ones. So the final step is an addition — and the thing being added is a number you have to work out first.'),
          steps([
            { label: 'Hidden first question: how many pencils in the boxes?', latex: '4 \\times 6 = 24' },
            { label: 'Now the question that was actually asked.', latex: '24 + 5 = 29' },
          ]),
          warning('24 is a correct number and a wrong answer. Stopping at the middle step is the commonest way to lose marks in this topic — and it feels like success, because the arithmetic was right.'),
          insight('Before you write anything down, say the answer as a sentence: "Maya has ___ pencils." If your number does not fit that sentence, you have answered a different question.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'tape-diagram', rows: [{ label: 'Boxes', parts: 4 }, { label: 'At home', parts: 1 }], unitLabel: 'each box holds 6' }, 'Four equal parts, then one unequal one. Two different operations, in that order.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A class has 38 crayons. 6 are broken and thrown away. The rest are shared equally between 4 tables. How many crayons does each table get?'),
          steps([
            { label: 'What is the last thing that happens?', latex: '\\text{sharing between 4 tables}' },
            { label: 'So first, how many are there to share?', latex: '38 - 6 = 32' },
            { label: 'Now share them.', latex: '32 \\div 4 = 8' },
            { label: 'Answer the question that was asked.', latex: '8 \\text{ crayons per table}' },
          ]),
          tip('Sharing 38 between 4 gives a remainder, and sharing 32 does not. When a division comes out awkward in a two-step problem, it is often a sign you skipped the first step.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('two-step-problems', 4)] },
      independent: { title: 'On your own', blocks: [practice('two-step-problems', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('two-step-problems', 3, 'These divide before they multiply.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Ask what happens last; that is your second step.',
            'The first step makes a number the question did not give you.',
            'The middle number is never the answer.',
            'Say the answer as a sentence to check it fits.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------ fractions on the number line ----- */
  lesson({
    id: 'fractions-on-number-line-01',
    unitId: 'unit-fractions-meaning',
    conceptId: 'fractions-on-number-line',
    title: 'Fractions on the Number Line',
    summary: 'A fraction is not only a slice of pizza. It is a number, and it has a place of its own on the line.',
    objectives: [
      'Locate a fraction on a number line between 0 and 1',
      'Count intervals rather than tick marks',
      'Place fractions greater than 1',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'From slices to numbers',
        blocks: [
          text('So far a fraction has been a part of a *thing* — a piece of a cake, a section of a bar. That is a good start and a limited one, because it makes a fraction sound like an adjective.'),
          text('Three quarters is a number. It sits between 0 and 1, closer to 1, at a definite place. Putting it on the number line is what turns it from a description into a quantity you can compare, order and eventually add.'),
        ],
      },
      intuition: {
        title: 'Cut the unit, then count along',
        blocks: [
          text('To find 3/4, take the distance from 0 to 1 — the *unit* — and cut it into 4 equal pieces. Each piece is one quarter. Then count 3 of them from 0.'),
          text('The denominator tells you how to cut. The numerator tells you how far to walk.'),
          warning('Count the gaps, not the marks. Cutting 0 to 1 into 4 equal pieces produces 5 marks, and a student counting marks calls the pieces fifths.'),
          insight('This is why 3/4 and 6/8 land on exactly the same spot. They are two descriptions of one place — which is the whole idea of equivalence, arriving early and for free.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'number-line', from: 0, to: 1, step: 0.25, labelAs: 'fraction', marks: [{ at: 0.75, label: '3/4' }] }, 'Four equal gaps, five marks. Three gaps along is 3/4.'),
          figure({ kind: 'number-line', from: 0, to: 2, step: 0.25, labelAs: 'fraction', marks: [{ at: 1.5, label: '6/4' }] }, 'Past 1, the gaps keep the same size and the counting keeps going.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Where does 5/6 sit on a number line from 0 to 1?'),
          steps([
            { label: 'The denominator says how to cut the unit.', latex: '6 \\text{ equal parts}' },
            { label: 'Each part is this size.', latex: '\\frac{1}{6}' },
            { label: 'The numerator says how many to count.', latex: '5 \\text{ parts from } 0' },
            { label: 'It lands one part short of 1.', latex: '\\frac{5}{6}' },
          ]),
          tip('Fractions just below 1 are easiest found from the top: 5/6 is one sixth short of 1, which is quicker than counting five steps up from 0.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fractions-on-number-line', 4)] },
      independent: { title: 'On your own', blocks: [practice('fractions-on-number-line', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('fractions-on-number-line', 3, 'These marks lie past 1.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A fraction is a number with a place on the line.',
            'The denominator cuts the unit; the numerator counts along.',
            'Count gaps, not marks.',
            'Past 1 the gaps stay the same size and the count carries on.',
          ]),
          insight('Every fraction skill after this one — comparing, adding, and eventually negative and irrational numbers — lives on this line. It is the most reused picture in mathematics.'),
        ],
      },
    },
  }),

  lesson({
    id: 'whole-number-fractions-01',
    unitId: 'unit-fractions-meaning',
    conceptId: 'whole-number-fractions',
    title: 'Fractions and Whole Numbers',
    summary: 'Every whole number is a fraction, and some fractions land exactly on one.',
    objectives: [
      'Recognise that n/n is 1 and n/1 is n',
      'Write a whole number as a fraction with a given denominator',
      'Locate whole numbers on a number line marked in fractions',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The two easy fractions everyone gets wrong',
        blocks: [
          text('4/4 and 4/1 look almost identical and mean completely different things. One is 1 and the other is 4.'),
          text('Getting these two right is worth more than it looks, because it forces the question "which number is doing which job?" — and that question is the whole of fraction notation.'),
        ],
      },
      intuition: {
        title: 'The bottom says how big, the top says how many',
        blocks: [
          text('In 4/4, the pieces are quarters and you have four of them. Four quarters is the whole thing, so 4/4 = 1.'),
          text('In 4/1, the pieces are *whole ones* — cutting something into 1 piece leaves it alone — and you have four of them. So 4/1 = 4.'),
          math('\\frac{4}{4} = 1 \\qquad \\frac{4}{1} = 4'),
          insight('That means every whole number is already a fraction. 7 is 7/1, and it has been all along; we simply do not usually write it that way.'),
          text('It also means a whole number can be written with any denominator you like, which is exactly the trick needed later to add 2 and 1/3.'),
          math('3 = \\frac{6}{2} = \\frac{9}{3} = \\frac{12}{4}'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'fraction-bars', bars: [{ parts: 4, shaded: 4, label: '4/4 = 1' }, { parts: 1, shaded: 1, label: '1/1 = 1' }] }),
          figure({ kind: 'number-line', from: 0, to: 2, step: 1 / 3, labelAs: 'fraction', marks: [{ at: 1, label: '3/3' }, { at: 2, label: '6/3' }] }, 'On a line marked in thirds, the whole numbers have fraction names too.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write 3 as a fraction with denominator 5.'),
          steps([
            { label: 'How many fifths make one whole?', latex: '1 = \\frac{5}{5}' },
            { label: 'You need three wholes.', latex: '3 \\times 5 = 15' },
            { label: 'Write it.', latex: '3 = \\frac{15}{5}' },
            { label: 'Check on the line.', latex: '15 \\text{ steps of } \\tfrac{1}{5} \\text{ reaches } 3' },
          ]),
          warning('The denominator is the one you were given, and it does not change. Only the numerator is being worked out.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('whole-number-fractions', 4)] },
      independent: { title: 'On your own', blocks: [practice('whole-number-fractions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('whole-number-fractions', 3, 'Read the whole number off a line marked in fractions.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'n/n = 1: all the pieces is the whole thing.',
            'n/1 = n: the pieces are whole ones.',
            'Every whole number can be written with any denominator.',
            'The denominator you are given does not change.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------ measurement ---- */
  lesson({
    id: 'telling-time-01',
    unitId: 'unit-time-measurement',
    conceptId: 'telling-time',
    title: 'Telling the Time',
    summary: 'One dial, two hands, and two completely different rules for reading them.',
    objectives: [
      'Read an analogue clock to the nearest five minutes and to the minute',
      'Explain why the numeral the long hand points at is not the number of minutes',
      'Tell which hand is which and why the short one names the hour',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Why a clock is confusing',
        blocks: [
          text('A clock face has twelve numerals on it, and it is asking you to read sixty minutes. That mismatch is the entire difficulty, and it is worth saying out loud rather than working round.'),
          text('The two hands do not disagree. They are answering two different questions on the same dial.'),
        ],
      },
      intuition: {
        title: 'Two hands, two rules',
        blocks: [
          text('The **short hand** names the hour. It moves slowly, and it names the hour it has *already passed* — a short hand between the 3 and the 4 means it is still three-something.'),
          text('The **long hand** counts minutes. It goes all the way round in one hour, so each numeral it passes is five minutes, not one.'),
          math('7 \\text{ on the dial} \\;\\longrightarrow\\; 7 \\times 5 = 35 \\text{ minutes past}'),
          warning('Reading the long hand as a plain number is the most common mistake there is. On the 7 it means 35 minutes, and calling it "7 minutes past" is out by nearly half an hour.'),
          insight('The small marks between the numerals are single minutes. Counting round in fives to the nearest numeral and then in ones is faster and safer than counting sixty marks.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'clock', hour: 3, minute: 35, label: '3:35' }, 'Short hand past the 3; long hand on the 7, which is 35 minutes.'),
          figure({ kind: 'clock', hour: 11, minute: 50, label: '11:50' }, 'Short hand nearly at 12 — but it has not arrived, so the hour is still 11.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Read the clock: the short hand sits between the 8 and the 9, and the long hand points at the 4.'),
          steps([
            { label: 'The short hand names the hour it has passed.', latex: '8' },
            { label: 'The long hand is on this numeral.', latex: '4' },
            { label: 'Each numeral is five minutes.', latex: '4 \\times 5 = 20' },
            { label: 'Read the time.', latex: '8{:}20' },
          ]),
          tip('If the long hand is past the 6, the short hand will be nearer the *next* numeral than the one that names the hour. That is normal, and it catches people out every time.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('telling-time', 4)] },
      independent: { title: 'On your own', blocks: [practice('telling-time', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('telling-time', 3, 'These are not on a five-minute mark.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Short hand: the hour it has already passed.',
            'Long hand: minutes, five for every numeral.',
            'Small marks are single minutes.',
            'Find the hour first, then the minutes.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'elapsed-time-01',
    unitId: 'unit-time-measurement',
    conceptId: 'elapsed-time',
    title: 'Elapsed Time',
    summary: 'Working out how long something took — counting in sixties, where arithmetic stops behaving like arithmetic.',
    objectives: [
      'Find the time elapsed between two clock times',
      'Find a finishing time from a start time and a duration',
      'Avoid treating minutes as if there were a hundred in an hour',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The one place where 60 replaces 10',
        blocks: [
          text('Every number you have met so far has been built out of tens. Time is not. An hour holds 60 minutes, so the moment after 3:59 is 4:00, and column subtraction quietly gives the wrong answer.'),
          text('There is no trick to memorise here. There is one habit: go up to the next whole hour first.'),
        ],
      },
      intuition: {
        title: 'Count on, in three hops',
        blocks: [
          text('From 2:40 to 4:15, take three hops instead of one subtraction.'),
          steps([
            { label: 'Up to the next whole hour.', latex: '2{:}40 \\rightarrow 3{:}00 \\;=\\; 20 \\text{ min}' },
            { label: 'Whole hours after that.', latex: '3{:}00 \\rightarrow 4{:}00 \\;=\\; 60 \\text{ min}' },
            { label: 'The last few minutes.', latex: '4{:}00 \\rightarrow 4{:}15 \\;=\\; 15 \\text{ min}' },
            { label: 'Add the hops.', latex: '20 + 60 + 15 = 95 \\text{ minutes}' },
          ]),
          warning('Subtracting 240 from 415 gives 175, and it is wrong by 80 minutes. Clock times are not four-digit numbers, however much they look like them.'),
          insight('The same habit works forwards. Adding 25 minutes to 3:50 means using 10 of them to reach 4:00 and the other 15 after it: 4:15, never 3:75.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'number-line', from: 0, to: 120, step: 15, marks: [{ at: 0, label: '2:40' }, { at: 20, label: '3:00' }, { at: 80, label: '4:00' }, { at: 95, label: '4:15' }] }, 'The three hops laid out as one journey of 95 minutes.'),
          figure({ kind: 'clock', hour: 2, minute: 40, label: 'start' }),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A swimming lesson starts at 4:45 and lasts 50 minutes. When does it end?'),
          steps([
            { label: 'How far to the next whole hour?', latex: '60 - 45 = 15 \\text{ min}' },
            { label: 'Spend those first.', latex: '4{:}45 + 15 = 5{:}00' },
            { label: 'Minutes still to spend.', latex: '50 - 15 = 35' },
            { label: 'Spend them after the hour.', latex: '5{:}35' },
          ]),
          tip('Check the answer is believable: 50 minutes is nearly an hour, so an end time a little before 5:45 is right. An answer of 4:95 is not a time at all.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('elapsed-time', 4)] },
      independent: { title: 'On your own', blocks: [practice('elapsed-time', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('elapsed-time', 3, 'These cross more than one hour.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An hour is 60 minutes, not 100.',
            'Count up to the next whole hour first.',
            'Then whole hours, then the remaining minutes.',
            'Minutes never reach 60 — trade them for an hour.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'mass-and-capacity-01',
    unitId: 'unit-time-measurement',
    conceptId: 'mass-and-capacity',
    title: 'Mass and Liquid Volume',
    summary: 'Grams and kilograms, millilitres and litres — and why a number without a unit is not an answer.',
    objectives: [
      'Convert between grams and kilograms, and millilitres and litres',
      'Compare two measurements given in different units',
      'Choose a sensible unit for a given object',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Two is not more than one thousand',
        blocks: [
          text('A 2 kilogram melon is heavier than an 850 gram pineapple, even though 2 is smaller than 850. The numbers cannot be compared until they are counting the same thing.'),
          text('This is the first topic where an answer needs a unit attached to be an answer at all.'),
        ],
      },
      intuition: {
        title: 'One thousand, twice',
        blocks: [
          text('Metric units are built to be easy: the big unit is a thousand of the small one, in both families.'),
          table(['Measures', 'Small unit', 'Big unit', 'Relationship'], [
            ['Mass', 'gram (g)', 'kilogram (kg)', '1 kg = 1000 g'],
            ['Liquid volume', 'millilitre (mL)', 'litre (L)', '1 L = 1000 mL'],
          ]),
          insight('The prefixes say it out loud. "Kilo" means a thousand of them; "milli" means a thousandth of one. Once you know that, you have not memorised two facts but one.'),
          text('Going to the smaller unit multiplies by 1000, and the number gets bigger. Going to the larger unit divides, and the number gets smaller. If your answer went the wrong way, you converted the wrong way.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'tape-diagram', rows: [{ label: '1 kg', parts: 10 }, { label: '100 g', parts: 1 }], unitLabel: 'each part is 100 g' }, 'Ten hundreds make the kilogram.'),
          figure({ kind: 'bar-chart', bars: [{ label: 'Melon', value: 2000 }, { label: 'Pineapple', value: 850 }], unitLabel: 'mass in grams' }, 'Once both are in grams, the comparison is obvious.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A jug holds 3 litres. How many 250 millilitre glasses can be filled from it?'),
          steps([
            { label: 'Put both measurements in one unit.', latex: '3 \\times 1000 = 3000 \\text{ mL}' },
            { label: 'Now the division makes sense.', latex: '3000 \\div 250' },
            { label: 'Answer.', latex: '12 \\text{ glasses}' },
          ]),
          warning('Dividing 3 by 250 before converting gives a number smaller than 1, which cannot be a count of glasses. Sense-check the answer against the situation, not just the arithmetic.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('mass-and-capacity', 4)] },
      independent: { title: 'On your own', blocks: [practice('mass-and-capacity', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('mass-and-capacity', 3, 'These mix the two units in one question.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '1 kg = 1000 g and 1 L = 1000 mL.',
            'Convert before you compare, add or divide.',
            'To the smaller unit: multiply, and the number grows.',
            'An answer without a unit is not an answer.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------------ data ----- */
  lesson({
    id: 'scaled-graphs-01',
    unitId: 'unit-data-grade-3',
    conceptId: 'scaled-graphs',
    title: 'Picture and Bar Graphs',
    summary: 'One symbol can stand for five things. Read the key before you read the graph.',
    objectives: [
      'Read a picture graph whose symbol represents several items',
      'Read values from a bar graph and compare them',
      'Answer "how many more" and "how many altogether" from a graph',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'A graph is an argument',
        blocks: [
          text('A graph exists to make a comparison visible at a glance — who has the most, how much more, whether things are close. Reading one is not decoration; it is the fastest way to answer a question about a list of numbers.'),
          text('But a graph can only be read once you know what one symbol, or one square of height, is worth.'),
        ],
      },
      intuition: {
        title: 'The key does the multiplying',
        blocks: [
          text('Drawing 35 books one at a time would be absurd, so a picture graph draws 7 symbols and says, in a key, that each one stands for 5 books.'),
          math('7 \\text{ symbols} \\times 5 \\text{ books each} = 35 \\text{ books}'),
          warning('Ignoring the key and answering 7 is the mistake this whole topic exists to prevent. The picture is deliberately smaller than the data.'),
          text('Bar graphs do the same thing with a scale up the side. A bar reaching the fourth line is not 4 unless the scale says so.'),
          insight('Two different questions get asked of every graph. "How many altogether" adds the bars; "how many more" subtracts two of them. Choosing the wrong one is far more common than misreading a bar.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'bar-chart', bars: [{ label: 'Red', value: 8 }, { label: 'Blue', value: 12 }, { label: 'Green', value: 5 }, { label: 'Yellow', value: 9 }], unitLabel: 'favourite colour' }, 'Blue has 12 and Green has 5: seven more chose blue.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('In a picture graph each symbol stands for 4 goals. Dara\'s row has 6 symbols and Eli\'s has 2. How many more goals did Dara score?'),
          steps([
            { label: 'Use the key on the first row.', latex: '6 \\times 4 = 24' },
            { label: 'And on the second.', latex: '2 \\times 4 = 8' },
            { label: '"How many more" is a difference.', latex: '24 - 8 = 16' },
          ]),
          tip('You could also work with symbols first — 4 more symbols, at 4 goals each, is 16. Both are right, and agreeing with yourself twice is a good check.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('scaled-graphs', 4)] },
      independent: { title: 'On your own', blocks: [practice('scaled-graphs', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('scaled-graphs', 3, 'Total every bar on the graph.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Read the key or the scale first.',
            'Symbols are multiplied, never counted.',
            '"How many more" subtracts; "altogether" adds.',
            'Sense-check the answer against the picture.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'line-plots-01',
    unitId: 'unit-data-grade-3',
    conceptId: 'line-plots',
    title: 'Line Plots',
    summary: 'One dot per measurement, stacked above its value — and the shape of the data appears on its own.',
    objectives: [
      'Read how often a value occurs from a line plot',
      'Identify the most common measurement',
      'Find the spread between the largest and smallest',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'A list you can look at',
        blocks: [
          text('Twenty pencils are measured and the lengths written in a list. The list is true and almost useless: nothing about it shows that most pencils are around 12 cm and one is unusually short.'),
          text('Put one dot above each length on a number line and all of that becomes visible without any calculation at all.'),
        ],
      },
      intuition: {
        title: 'Two different numbers on one picture',
        blocks: [
          text('A line plot has numbers running along the bottom and dots stacked above them. These are not the same kind of number, and keeping them apart is the whole skill.'),
          list([
            'The number along the bottom is the measurement — 12 cm.',
            'The number of dots above it is how often that measurement happened.',
          ]),
          warning('"How many pencils were 12 cm?" wants the dots. "Which length was most common?" wants the number underneath. Answering with the other one is the standard error here.'),
          insight('The tallest stack is the most common value. The distance from the leftmost dot to the rightmost is the spread — how much the measurements vary.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'dot-plot', values: [10, 11, 11, 12, 12, 12, 12, 12, 13, 13, 14], from: 10, to: 14, label: 'length in cm' }, 'Five pencils were 12 cm; the lengths run from 10 cm to 14 cm.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Using the plot above: how many pencils were 13 cm, and what is the difference between the longest and shortest?'),
          steps([
            { label: 'Find 13 along the bottom and count upwards.', latex: '2 \\text{ pencils}' },
            { label: 'The rightmost dot.', latex: '14 \\text{ cm}' },
            { label: 'The leftmost dot.', latex: '10 \\text{ cm}' },
            { label: 'Subtract for the spread.', latex: '14 - 10 = 4 \\text{ cm}' },
          ]),
          tip('Count the dots altogether and check it matches how many things were measured. If it does not, you have missed a stack.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('line-plots', 4)] },
      independent: { title: 'On your own', blocks: [practice('line-plots', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('line-plots', 3, 'Find the spread of the data.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Along the bottom: the measurement. Up the stack: how often.',
            'The tallest stack is the most common value.',
            'Spread is largest minus smallest.',
            'Total the dots to check nothing was missed.',
          ]),
          insight('This picture becomes the dot plot of Grade 6 statistics and then the histogram. Nothing about it changes; only the questions asked of it get harder.'),
        ],
      },
    },
  }),

  /* -------------------------------------------------------- geometry ----- */
  lesson({
    id: 'shape-attributes-01',
    unitId: 'unit-shapes',
    conceptId: 'shape-attributes',
    title: 'Shapes and Their Attributes',
    summary: 'A shape is named by what is true of it — not by what it looks like from where you are standing.',
    objectives: [
      'Name a polygon from its number of sides',
      'List the attributes that define each quadrilateral',
      'Explain why every square is a rectangle',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Naming by rule, not by eye',
        blocks: [
          text('Ask a class to point at the square and everyone agrees. Turn it forty-five degrees and half of them call it a diamond. Nothing about the shape changed — only the way it was sitting.'),
          text('Geometry fixes that by naming shapes with a checklist instead of a glance.'),
        ],
      },
      intuition: {
        title: 'Sides, corners and angles come in threes',
        blocks: [
          text('A polygon is a closed shape made of straight sides. Count the sides and you have counted the corners and the angles too — they always match, because each side ends at a corner and each corner holds one angle.'),
          table(['Sides', 'Name'], [['3', 'Triangle'], ['4', 'Quadrilateral'], ['5', 'Pentagon'], ['6', 'Hexagon'], ['8', 'Octagon']]),
          text('Quadrilaterals then get sorted by what else is true of them: which sides are parallel, which are equal, whether the angles are right angles.'),
          insight('These groups sit inside one another. A rectangle is any quadrilateral with four right angles — and a square has four right angles, so every square *is* a rectangle. A square is a rectangle that also has equal sides.'),
          warning('"It cannot be a rectangle, it is a square" sounds sensible and is false. Extra properties never disqualify a shape from a family; they only make it a more special member of it.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'polygon', sides: 6 }, 'Six sides, six corners, six angles — a hexagon, whichever way it is turned.'),
          figure({ kind: 'shape', shape: 'rectangle', labels: { width: 'equal', height: 'equal' } }, 'Four right angles makes it a rectangle. Equal sides as well makes it a square.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A quadrilateral has four equal sides but no right angles. What is it?'),
          steps([
            { label: 'Four sides.', latex: '\\text{quadrilateral}' },
            { label: 'All four equal — that narrows it.', latex: '\\text{square or rhombus}' },
            { label: 'No right angles rules one out.', latex: '\\text{not a square}' },
            { label: 'Name it.', latex: '\\text{rhombus}' },
          ]),
          tip('Work through the attributes one at a time and cross out every shape that fails one. Naming by elimination is more reliable than naming by recognition.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('shape-attributes', 4)] },
      independent: { title: 'On your own', blocks: [practice('shape-attributes', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('shape-attributes', 3, 'Decide whether one family contains another.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Sides, corners and angles always match in number.',
            'Shapes are named by attributes, not by orientation.',
            'Every square is a rectangle and a rhombus.',
            'Extra properties make a shape more special, never less.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'equal-shares-01',
    unitId: 'unit-shapes',
    conceptId: 'equal-shares',
    title: 'Equal Shares of a Shape',
    summary: 'Cut a shape into equal parts and each part has a name — but only if the parts really are equal.',
    objectives: [
      'Partition a shape into equal parts and name one part',
      'Recognise when parts are not equal and why it matters',
      'Name the fraction of a shape that remains',
    ],
    estimatedMinutes: 20,
    sections: {
      introduction: {
        title: 'The word that does all the work',
        blocks: [
          text('A pizza cut into 4 pieces gives quarters — provided the four pieces are the same size. Cut it into one huge piece and three slivers and you still have four pieces, but not one of them is a quarter.'),
          text('"Equal" is not a detail in the definition of a fraction. It is the definition.'),
        ],
      },
      intuition: {
        title: 'Counting the parts of the whole',
        blocks: [
          text('When a shape is cut into equal parts, the number of parts is the denominator and one part is one of them.'),
          math('6 \\text{ equal parts} \\;\\longrightarrow\\; \\text{each is } \\tfrac{1}{6}'),
          text('Equal parts do not have to be the same *shape*. A square cut into two triangles by a diagonal gives two halves, and they look nothing alike — they simply cover the same amount.'),
          insight('This is also how a rectangle split into a grid works. 3 rows of 4 gives 12 equal squares, so one square is one twelfth — not one seventh, which is what adding the rows and columns would give.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'fraction-bar', parts: 6, shaded: 1, label: 'one sixth' }),
          figure({ kind: 'area-model', rows: 3, columns: 4, shadedRows: 1, shadedColumns: 1 }, 'Twelve equal squares, so one of them is 1/12.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A cake is cut into 8 equal slices and 3 are eaten. What fraction is left?'),
          steps([
            { label: 'How many slices remain?', latex: '8 - 3 = 5' },
            { label: 'Out of how many in the whole cake?', latex: '8' },
            { label: 'Write the fraction.', latex: '\\frac{5}{8}' },
          ]),
          warning('The denominator stays 8. Eating slices does not make the remaining ones bigger, so it cannot change the size of a piece.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('equal-shares', 4)] },
      independent: { title: 'On your own', blocks: [practice('equal-shares', 4)] },
      challenge: { title: 'Something harder', blocks: [practice('equal-shares', 3, 'These are cut into a grid rather than a row.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Parts must be equal in amount, not in shape.',
            'The number of equal parts is the denominator.',
            'A grid of r rows and c columns has r × c equal parts.',
            'Taking parts away does not change the denominator.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'area-composite-01',
    unitId: 'unit-measurement',
    conceptId: 'area-composite',
    title: 'Area of Composite Figures',
    summary: 'A shape that is not a rectangle is usually rectangles in disguise.',
    objectives: [
      'Find the area of an L-shape by splitting it into rectangles',
      'Find an area by subtracting a missing corner',
      'Use the area model to see why a factor can be split',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Only one formula, used twice',
        blocks: [
          text('You know the area of a rectangle. That turns out to be enough for a surprising number of shapes, because most of the awkward ones can be cut into rectangles — or completed into one.'),
        ],
      },
      intuition: {
        title: 'Split it, or fill it in',
        blocks: [
          text('An L-shape can be handled two ways, and both are correct.'),
          list([
            'Split: cut it into two rectangles, find each area, and add.',
            'Subtract: imagine the missing corner filled in, find the whole rectangle, and take the corner away.',
          ], true),
          insight('Areas of separate pieces always add, because area counts unit squares and a square belongs to exactly one piece. That is the only fact this topic needs.'),
          warning('The commonest error is finding the surrounding rectangle and stopping. It is a useful number, but it counts space the shape does not cover.'),
          text('The same picture explains the splitting property of multiplication. A 7 by 13 rectangle cut into 7 by 10 and 7 by 3 has not changed area, so 7 × 13 must equal 7 × 10 + 7 × 3.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({
            kind: 'grid',
            width: 6,
            height: 4,
            shaded: [
              [0, 0], [1, 0], [2, 0],
              [0, 1], [1, 1], [2, 1],
              [0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2],
              [0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3],
            ],
          }, 'Either two rectangles (3×2 and 6×2), or a 6×4 rectangle with a 3×2 corner missing.'),
          figure({ kind: 'area-model', rows: 7, columns: 13, shadedRows: 7, shadedColumns: 10 }, 'The same idea as multiplication: 7 × 13 is 7 × 10 plus 7 × 3.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A rectangle is 8 cm by 5 cm, with a 3 cm by 2 cm corner cut out. What is the area of what is left?'),
          steps([
            { label: 'The whole rectangle, corner included.', latex: '8 \\times 5 = 40' },
            { label: 'The corner that is missing.', latex: '3 \\times 2 = 6' },
            { label: 'Subtract.', latex: '40 - 6 = 34' },
            { label: 'Answer, in square units.', latex: '34 \\text{ cm}^2' },
          ]),
          tip('Check by the other method. Splitting the same shape into two rectangles must give 34 as well — and if it does not, one of your side lengths is wrong.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('area-composite', 4)] },
      independent: { title: 'On your own', blocks: [practice('area-composite', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('area-composite', 3, 'Count the squares to check your arithmetic.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Split into rectangles and add, or complete the rectangle and subtract.',
            'Areas of separate pieces always add.',
            'The surrounding rectangle is not the answer.',
            'Check with the other method.',
          ]),
        ],
      },
    },
  }),
];
